// src/screens/EventsManagement.jsx — Events CRUD + Tiers + Screenings
import "bootstrap-icons/font/bootstrap-icons.css";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import Swal from "sweetalert2";
import "../css/EventsBookings.css";
import useIdleLogout from "../hooks/useIdleLogout";

ChartJS.register(ArcElement, Tooltip, Legend);

const EVENTS_BASE = (process.env.REACT_APP_EVENTS_API_URL || "").replace(/\/+$/, "");
const PAGE_LIMIT = 20;
const CATEGORIES = ["cinema", "concert", "festival", "workshop", "experience"];
const PALETTE = ["#0d6efd","#198754","#dc3545","#ffc107","#20c997","#6f42c1","#fd7e14","#0dcaf0","#6610f2","#1982c4"];
const CATEGORY_COLORS = { cinema:"#0d6efd", concert:"#198754", festival:"#dc3545", workshop:"#ffc107", experience:"#20c997" };
const BADGE_COLOR = { cinema:"primary", concert:"success", festival:"danger", workshop:"warning", experience:"info" };

function getBearerToken() {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token") || localStorage.getItem("authToken") || sessionStorage.getItem("access_token") || sessionStorage.getItem("token");
  if (t) return String(t).trim();
  try { const u = JSON.parse(localStorage.getItem("user") || "{}"); return String(u?.access_token || u?.token || "").trim(); } catch { return ""; }
}
function getAuthHeaders(json = false) {
  const h = { Accept: "application/json" };
  const t = getBearerToken();
  if (t) h.Authorization = `Bearer ${t}`;
  if (json) h["Content-Type"] = "application/json";
  return h;
}
const fmt = (n) => `BTN. ${Number(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtDate = (ts) => ts ? new Date(ts).toLocaleString("en-US",{timeZone:"Asia/Thimphu",month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit",hour12:true}) : "—";
const pad2 = (n) => String(n).padStart(2,"0");
const todayYMD = () => { const d=new Date(); return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`; };

const LOGS_API = (process.env.REACT_APP_ADMIN_LOGS_API_URL || "").trim();

function postLog(description) {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const name = user?.user_name || user?.name || "Admin";
    const token = localStorage.getItem("access_token") || "";
    fetch(LOGS_API, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ description, user: name, admin_name: name, timestamp: new Date().toISOString() }),
    }).catch(() => {});
  } catch { }
}

async function apiCall(method, path, body) {
  if (!EVENTS_BASE) throw new Error("Events API URL not configured.");
  const opts = { method, headers: getAuthHeaders(!!body), cache: "no-store" };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${EVENTS_BASE}${path}`, opts);
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
  return json;
}

const CATS_HTML = CATEGORIES.map(c => `<option value="${c}">${c.charAt(0).toUpperCase()+c.slice(1)}</option>`).join("");

export default function EventsManagement() {
  useIdleLogout();

  const [events, setEvents]     = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState("");
  const [search, setSearch]     = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate]     = useState("");
  const [category, setCategory] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [savingId, setSavingId]     = useState(null);

  const fetchEvents = useCallback(async (q, cat, from, to, pg) => {
    if (!EVENTS_BASE) { setErr("Events API URL not configured."); return; }
    try {
      setLoading(true); setErr("");
      const url = new URL(`${EVENTS_BASE}/events`);
      if (q)    url.searchParams.set("q", q);
      if (cat)  url.searchParams.set("category", cat);
      if (from) url.searchParams.set("from_date", from);
      if (to)   url.searchParams.set("to_date", to);
      url.searchParams.set("page", String(pg));
      url.searchParams.set("limit", String(PAGE_LIMIT));
      url.searchParams.set("sort", "start_at");
      const res = await fetch(url.toString(), { headers: getAuthHeaders(), cache: "no-store" });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
      const rows = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
      setEvents(rows);
      setTotal(json?.total ?? rows.length);
    } catch (e) { setErr(e.message || "Failed to load events."); setEvents([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents("","",fromDate,toDate,1); }, []); // eslint-disable-line

  const refresh = () => fetchEvents(search, category, fromDate, toDate, page);
  const handleApply = () => { setPage(1); fetchEvents(search, category, fromDate, toDate, 1); };
  const handlePage  = (p)  => { setPage(p); fetchEvents(search, category, fromDate, toDate, p); };

  /* -------- CREATE EVENT -------- */
  const handleCreate = async () => {
    const { value: fd, isConfirmed } = await Swal.fire({
      title: "Create Event", width: "720px", showCancelButton: true, confirmButtonText: "Create",
      html: `<div class="row g-2 text-start" style="font-size:14px">
        <div class="col-8"><label class="form-label fw-semibold">Title *</label><input id="ev-title" class="form-control form-control-sm"></div>
        <div class="col-4"><label class="form-label fw-semibold">Category *</label><select id="ev-cat" class="form-select form-select-sm"><option value="">-- Select --</option>${CATS_HTML}</select></div>
        <div class="col-6"><label class="form-label fw-semibold">Venue Name *</label><input id="ev-venue" class="form-control form-control-sm"></div>
        <div class="col-6"><label class="form-label fw-semibold">City</label><input id="ev-city" class="form-control form-control-sm" placeholder="Thimphu"></div>
        <div class="col-12"><label class="form-label fw-semibold">Venue Address</label><input id="ev-addr" class="form-control form-control-sm"></div>
        <div class="col-6"><label class="form-label fw-semibold">Organizer Name</label><input id="ev-org" class="form-control form-control-sm"></div>
        <div class="col-6"><label class="form-label fw-semibold">Cover Image URL</label><input id="ev-img" class="form-control form-control-sm" placeholder="https://..."></div>
        <div class="col-6"><label class="form-label fw-semibold">Start Date &amp; Time *</label><input id="ev-start" type="datetime-local" class="form-control form-control-sm"></div>
        <div class="col-6"><label class="form-label fw-semibold">End Date &amp; Time</label><input id="ev-end" type="datetime-local" class="form-control form-control-sm"></div>
        <div class="col-12"><label class="form-label fw-semibold">Description</label><textarea id="ev-desc" class="form-control form-control-sm" rows="2"></textarea></div>
        <div class="col-12"><div class="form-check mt-1"><input type="checkbox" id="ev-live" class="form-check-input"><label class="form-check-label" for="ev-live">Set as Live / Featured</label></div></div>
      </div>`,
      preConfirm: () => {
        const title = document.getElementById("ev-title").value.trim();
        const category = document.getElementById("ev-cat").value;
        const venue_name = document.getElementById("ev-venue").value.trim();
        const start_at = document.getElementById("ev-start").value;
        if (!title)     { Swal.showValidationMessage("Title is required"); return false; }
        if (!category)  { Swal.showValidationMessage("Category is required"); return false; }
        if (!venue_name){ Swal.showValidationMessage("Venue name is required"); return false; }
        if (!start_at)  { Swal.showValidationMessage("Start date/time is required"); return false; }
        const endVal = document.getElementById("ev-end").value;
        return {
          title, category, venue_name,
          city:           document.getElementById("ev-city").value.trim() || undefined,
          venue_address:  document.getElementById("ev-addr").value.trim() || undefined,
          organizer_name: document.getElementById("ev-org").value.trim()  || undefined,
          cover_image:    document.getElementById("ev-img").value.trim()  || undefined,
          start_at:       new Date(start_at).toISOString(),
          end_at:         endVal ? new Date(endVal).toISOString() : undefined,
          description:    document.getElementById("ev-desc").value.trim() || undefined,
          is_live:        document.getElementById("ev-live").checked,
        };
      },
    });
    if (!isConfirmed || !fd) return;
    try {
      setSavingId("new");
      await apiCall("POST", "/events", fd);
      postLog(`Created event "${fd.title}" (${fd.category})`);
      Swal.fire({ icon:"success", title:"Event created!", timer:1500, showConfirmButton:false });
      refresh();
    } catch(e) { Swal.fire({ icon:"error", title:"Failed", text:e.message }); }
    finally { setSavingId(null); }
  };

  /* -------- EDIT EVENT -------- */
  const handleEdit = async (ev) => {
    const toLocal = (iso) => iso ? iso.slice(0,16) : "";
    const { value: fd, isConfirmed } = await Swal.fire({
      title: "Edit Event", width: "720px", showCancelButton: true, confirmButtonText: "Save",
      html: `<div class="row g-2 text-start" style="font-size:14px">
        <div class="col-8"><label class="form-label fw-semibold">Title *</label><input id="ev-title" class="form-control form-control-sm" value="${ev.title||""}"></div>
        <div class="col-4"><label class="form-label fw-semibold">Category *</label><select id="ev-cat" class="form-select form-select-sm"><option value="">-- Select --</option>${CATEGORIES.map(c=>`<option value="${c}" ${ev.category===c?"selected":""}>${c.charAt(0).toUpperCase()+c.slice(1)}</option>`).join("")}</select></div>
        <div class="col-6"><label class="form-label fw-semibold">Venue Name *</label><input id="ev-venue" class="form-control form-control-sm" value="${ev.venue_name||""}"></div>
        <div class="col-6"><label class="form-label fw-semibold">City</label><input id="ev-city" class="form-control form-control-sm" value="${ev.city||""}"></div>
        <div class="col-12"><label class="form-label fw-semibold">Venue Address</label><input id="ev-addr" class="form-control form-control-sm" value="${ev.venue_address||""}"></div>
        <div class="col-6"><label class="form-label fw-semibold">Organizer Name</label><input id="ev-org" class="form-control form-control-sm" value="${ev.organizer_name||""}"></div>
        <div class="col-6"><label class="form-label fw-semibold">Cover Image URL</label><input id="ev-img" class="form-control form-control-sm" value="${ev.cover_image||""}"></div>
        <div class="col-6"><label class="form-label fw-semibold">Start Date &amp; Time *</label><input id="ev-start" type="datetime-local" class="form-control form-control-sm" value="${toLocal(ev.start_at)}"></div>
        <div class="col-6"><label class="form-label fw-semibold">End Date &amp; Time</label><input id="ev-end" type="datetime-local" class="form-control form-control-sm" value="${toLocal(ev.end_at)}"></div>
        <div class="col-12"><label class="form-label fw-semibold">Description</label><textarea id="ev-desc" class="form-control form-control-sm" rows="2">${ev.description||""}</textarea></div>
        <div class="col-12"><div class="form-check mt-1"><input type="checkbox" id="ev-live" class="form-check-input" ${ev.is_live?"checked":""}><label class="form-check-label" for="ev-live">Set as Live / Featured</label></div></div>
      </div>`,
      preConfirm: () => {
        const title     = document.getElementById("ev-title").value.trim();
        const category  = document.getElementById("ev-cat").value;
        const venue_name= document.getElementById("ev-venue").value.trim();
        const start_at  = document.getElementById("ev-start").value;
        if (!title)      { Swal.showValidationMessage("Title is required"); return false; }
        if (!category)   { Swal.showValidationMessage("Category is required"); return false; }
        if (!venue_name) { Swal.showValidationMessage("Venue name is required"); return false; }
        if (!start_at)   { Swal.showValidationMessage("Start date/time is required"); return false; }
        const endVal = document.getElementById("ev-end").value;
        return {
          title, category, venue_name,
          city:           document.getElementById("ev-city").value.trim() || undefined,
          venue_address:  document.getElementById("ev-addr").value.trim() || undefined,
          organizer_name: document.getElementById("ev-org").value.trim()  || undefined,
          cover_image:    document.getElementById("ev-img").value.trim()  || undefined,
          start_at:       new Date(start_at).toISOString(),
          end_at:         endVal ? new Date(endVal).toISOString() : undefined,
          description:    document.getElementById("ev-desc").value.trim() || undefined,
          is_live:        document.getElementById("ev-live").checked,
        };
      },
    });
    if (!isConfirmed || !fd) return;
    try {
      setSavingId(ev.id);
      await apiCall("PATCH", `/events/${ev.id}`, fd);
      postLog(`Updated event "${fd.title}"`);
      Swal.fire({ icon:"success", title:"Event updated!", timer:1500, showConfirmButton:false });
      refresh();
    } catch(e) { Swal.fire({ icon:"error", title:"Failed", text:e.message }); }
    finally { setSavingId(null); }
  };

  /* -------- DELETE EVENT -------- */
  const handleDelete = async (ev) => {
    const { isConfirmed } = await Swal.fire({
      icon:"warning", title:`Delete "${ev.title}"?`,
      text: ev.booking_count > 0 ? `This event has ${ev.booking_count} booking(s). Deleting will also remove them.` : "This action cannot be undone.",
      showCancelButton:true, confirmButtonText:"Yes, Delete", confirmButtonColor:"#dc3545",
    });
    if (!isConfirmed) return;
    try {
      setSavingId(ev.id);
      const path = ev.booking_count > 0 ? `/events/${ev.id}?force=true` : `/events/${ev.id}`;
      await apiCall("DELETE", path);
      postLog(`Deleted event "${ev.title}"`);
      Swal.fire({ icon:"success", title:"Deleted!", timer:1200, showConfirmButton:false });
      if (expandedId === ev.id) setExpandedId(null);
      refresh();
    } catch(e) { Swal.fire({ icon:"error", title:"Failed", text:e.message }); }
    finally { setSavingId(null); }
  };

  /* -------- SET LIVE -------- */
  const handleSetLive = async (ev) => {
    const { isConfirmed } = await Swal.fire({
      icon:"question", title:`Set "${ev.title}" as live?`,
      text:"This will unset any currently live event.", showCancelButton:true, confirmButtonText:"Yes, Set Live",
    });
    if (!isConfirmed) return;
    try {
      setSavingId(ev.id);
      await apiCall("PATCH", `/events/${ev.id}/live`);
      postLog(`Set event "${ev.title}" as live`);
      Swal.fire({ icon:"success", title:"Now live!", timer:1200, showConfirmButton:false });
      refresh();
    } catch(e) { Swal.fire({ icon:"error", title:"Failed", text:e.message }); }
    finally { setSavingId(null); }
  };

  /* -------- ADD TIER -------- */
  const handleAddTier = async (eventId) => {
    const { value: fd, isConfirmed } = await Swal.fire({
      title:"Add Ticket Tier", showCancelButton:true, confirmButtonText:"Add",
      html:`<div class="row g-2 text-start" style="font-size:14px">
        <div class="col-6"><label class="form-label fw-semibold">Tier Name *</label><input id="t-name" class="form-control form-control-sm" placeholder="e.g. VIP, Regular"></div>
        <div class="col-6"><label class="form-label fw-semibold">Price (BTN) *</label><input id="t-price" type="number" min="0" class="form-control form-control-sm"></div>
        <div class="col-6"><label class="form-label fw-semibold">Available Seats *</label><input id="t-seats" type="number" min="1" class="form-control form-control-sm"></div>
        <div class="col-6"><label class="form-label fw-semibold">Description</label><input id="t-desc" class="form-control form-control-sm" placeholder="Optional"></div>
      </div>`,
      preConfirm: () => {
        const name  = document.getElementById("t-name").value.trim();
        const price = Number(document.getElementById("t-price").value);
        const seats = Number(document.getElementById("t-seats").value);
        if (!name)       { Swal.showValidationMessage("Tier name is required"); return false; }
        if (isNaN(price)){ Swal.showValidationMessage("Price is required"); return false; }
        if (!seats)      { Swal.showValidationMessage("Available seats is required"); return false; }
        return { name, price, available_seats: seats, description: document.getElementById("t-desc").value.trim() || undefined };
      },
    });
    if (!isConfirmed || !fd) return;
    try {
      await apiCall("POST", `/events/${eventId}/tiers`, fd);
      postLog(`Added ticket tier "${fd.name}" to event`);
      Swal.fire({ icon:"success", title:"Tier added!", timer:1200, showConfirmButton:false });
      refresh();
    } catch(e) { Swal.fire({ icon:"error", title:"Failed", text:e.message }); }
  };

  /* -------- DELETE TIER -------- */
  const handleDeleteTier = async (tierId) => {
    const { isConfirmed } = await Swal.fire({ icon:"warning", title:"Delete this tier?", showCancelButton:true, confirmButtonColor:"#dc3545" });
    if (!isConfirmed) return;
    try {
      await apiCall("DELETE", `/tiers/${tierId}`);
      postLog(`Deleted ticket tier (ID: ${tierId})`);
      Swal.fire({ icon:"success", title:"Tier deleted!", timer:1200, showConfirmButton:false });
      refresh();
    } catch(e) { Swal.fire({ icon:"error", title:"Failed", text:e.message }); }
  };

  /* -------- ADD SCREENING -------- */
  const handleAddScreening = async (eventId) => {
    const { value: fd, isConfirmed } = await Swal.fire({
      title:"Add Screening", showCancelButton:true, confirmButtonText:"Add",
      html:`<div class="row g-2 text-start" style="font-size:14px">
        <div class="col-12"><label class="form-label fw-semibold">Hall ID *</label><input id="s-hall" class="form-control form-control-sm" placeholder="Hall UUID"></div>
        <div class="col-6"><label class="form-label fw-semibold">Show Date *</label><input id="s-date" type="date" class="form-control form-control-sm"></div>
        <div class="col-6"><label class="form-label fw-semibold">Show Time *</label><input id="s-time" type="time" class="form-control form-control-sm"></div>
      </div>`,
      preConfirm: () => {
        const hall_id   = document.getElementById("s-hall").value.trim();
        const show_date = document.getElementById("s-date").value;
        const show_time = document.getElementById("s-time").value;
        if (!hall_id)   { Swal.showValidationMessage("Hall ID is required"); return false; }
        if (!show_date) { Swal.showValidationMessage("Show date is required"); return false; }
        if (!show_time) { Swal.showValidationMessage("Show time is required"); return false; }
        return { hall_id, show_date, show_time };
      },
    });
    if (!isConfirmed || !fd) return;
    try {
      await apiCall("POST", `/events/${eventId}/screenings`, fd);
      postLog(`Added screening on ${fd.show_date} at ${fd.show_time} (event ID: ${eventId})`);
      Swal.fire({ icon:"success", title:"Screening added!", timer:1200, showConfirmButton:false });
    } catch(e) { Swal.fire({ icon:"error", title:"Failed", text:e.message }); }
  };

  /* -------- EXPORT -------- */
  const handleExportCSV = async () => {
    if (!EVENTS_BASE) return;
    try {
      const url = new URL(`${EVENTS_BASE}/revenue/export`);
      if (fromDate) url.searchParams.set("from_date", fromDate);
      if (toDate)   url.searchParams.set("to_date", toDate);
      const res = await fetch(url.toString(), { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `events_revenue_${todayYMD()}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch(e) { alert(e.message); }
  };

  /* -------- METRICS + CHARTS (from events data) -------- */
  const gross    = useMemo(() => events.reduce((s,e)=>s+Number(e.gross_revenue||0),0),[events]);
  const tickets  = useMemo(() => events.reduce((s,e)=>s+Number(e.tickets_sold||0),0),[events]);
  const bookings = useMemo(() => events.reduce((s,e)=>s+Number(e.booking_count||0),0),[events]);
  const avgTicket = tickets > 0 ? gross / tickets : 0;

  const metrics = useMemo(() => loading
    ? [{ label:"Gross Revenue",value:"…"},{ label:"Total Bookings",value:"…"},{ label:"Tickets Sold",value:"…"},{ label:"Avg Ticket Value",value:"…"}]
    : [{ label:"Gross Revenue",value:fmt(gross)},{ label:"Total Bookings",value:bookings.toLocaleString()},{ label:"Tickets Sold",value:tickets.toLocaleString()},{ label:"Avg Ticket Value",value:fmt(avgTicket)}]
  , [loading,gross,bookings,tickets,avgTicket]);

  const mkDonut = (entries, colorFn, tooltip) => {
    const filtered = entries.filter(([,v])=>v>0);
    if (!filtered.length) return { labels:["No Data"], datasets:[{data:[1],backgroundColor:["#dee2e6"]}] };
    return { labels:filtered.map(([k])=>k), datasets:[{data:filtered.map(([,v])=>v), backgroundColor:filtered.map(colorFn)}] };
  };

  const catEntries  = useMemo(()=>{ const m={}; events.forEach(e=>{const k=e.category||"other";m[k]=(m[k]||0)+Number(e.gross_revenue||0);}); return Object.entries(m); },[events]);
  const bkgEntries  = useMemo(()=>{ const m={}; events.forEach(e=>{const k=e.category||"other";m[k]=(m[k]||0)+Number(e.booking_count||0);}); return Object.entries(m); },[events]);
  const categoryChart = useMemo(()=>mkDonut(catEntries,([k])=>CATEGORY_COLORS[k]||PALETTE[0], true),[catEntries]);
  const bookingChart  = useMemo(()=>mkDonut(bkgEntries,([k],i)=>CATEGORY_COLORS[k]||PALETTE[i%PALETTE.length], false),[bkgEntries]);

  const donutOpts = (moneyTooltip) => ({
    responsive:true, maintainAspectRatio:true, layout:{padding:{bottom:10}},
    plugins:{ legend:{position:"bottom",labels:{boxWidth:12,padding:10,font:{size:11}}},
      tooltip:{callbacks:{label:(ctx)=>moneyTooltip?`${ctx.label}: ${fmt(ctx.raw??0)}`:`${ctx.label}: ${ctx.raw??0} bookings`}} },
  });

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  return (
    <>
      {err && <div className="alert alert-danger" role="alert">{err}</div>}

      {/* METRICS */}
      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-3 mb-4 events-metrics">
        {metrics.map((m,i)=>(
          <div className="col" key={i}>
            <div className="card metrics-card h-100">
              <div className="card-body">
                <h6 className="text-secondary mb-1">{m.label}</h6>
                <div className="fs-2 fw-bold">{m.value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CHARTS */}
      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="card chart-card h-100">
            <div className="card-header">Revenue by Category</div>
            <div className="card-body"><Doughnut data={categoryChart} options={donutOpts(true)} /></div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card chart-card h-100">
            <div className="card-header">Bookings by Category</div>
            <div className="card-body"><Doughnut data={bookingChart} options={donutOpts(false)} /></div>
          </div>
        </div>
      </div>

      {/* TOOLBAR */}
      <div className="card mb-3 search-filter-card">
        <div className="card-body searchbar-row-inline">
          <span className="search-icon"><i className="bi bi-search" /></span>
          <div className="search-wrap">
            <input type="text" className="form-control search-pill" placeholder="Search by title, venue, organizer…"
              value={search} onChange={e=>setSearch(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleApply()} />
          </div>
          <select className="select-pill" value={category} onChange={e=>setCategory(e.target.value)}>
            <option value="">All Categories</option>
            {CATEGORIES.map(c=><option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
          </select>
          <div className="date-range">
            <input type="date" className="date-pill" value={fromDate} onChange={e=>setFromDate(e.target.value)} />
            <span className="range-sep">to</span>
            <input type="date" className="date-pill" value={toDate} onChange={e=>setToDate(e.target.value)} />
          </div>
          <div className="exports ms-auto d-flex gap-2">
            <button className="btn btn-primary" onClick={handleApply}>Apply</button>
            <button className="btn btn-success" onClick={handleExportCSV}>Export CSV</button>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="bookings-table-card card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Events List</h5>
          <div className="d-flex align-items-center gap-2">
            <small className="text-white-50">{total} total</small>
            <button className="ev-header-btn" onClick={handleCreate} disabled={savingId==="new"}>
              <i className="bi bi-plus-lg" />New Event
            </button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle table-pro">
            <colgroup>
              <col style={{width:"60px"}} /><col style={{width:"280px"}} /><col style={{width:"110px"}} />
              <col style={{width:"170px"}} /><col style={{width:"175px"}} /><col style={{width:"90px"}} />
              <col style={{width:"90px"}} /><col style={{width:"145px"}} /><col style={{width:"70px"}} />
              <col style={{width:"200px"}} />
            </colgroup>
            <thead>
              <tr>
                <th>SI.NO</th><th>Event</th><th>Category</th><th>Venue / City</th>
                <th>Start Date &amp; Time</th><th>Tickets</th><th>Bookings</th>
                <th>Revenue (BTN)</th><th>Live</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="py-4 text-center text-muted"><span className="spinner-border spinner-border-sm me-2" />Loading…</td></tr>}
              {!loading && events.length===0 && <tr><td colSpan={10} className="text-center py-4 text-muted">No events found.</td></tr>}
              {!loading && events.map((ev,idx)=>(
                <>
                  <tr key={ev.id}>
                    <td>{(page-1)*PAGE_LIMIT+idx+1}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        {ev.cover_image && <img src={ev.cover_image} alt="" style={{width:32,height:32,objectFit:"cover",borderRadius:4,flexShrink:0}} />}
                        <div>
                          <div className="fw-semibold" style={{fontSize:13,lineHeight:1.3}}>{ev.title}</div>
                          {ev.organizer_name && <div className="text-muted" style={{fontSize:11}}>{ev.organizer_name}</div>}
                        </div>
                      </div>
                    </td>
                    <td><span className={`badge bg-${BADGE_COLOR[ev.category]||"secondary"}`}>{ev.category||"—"}</span></td>
                    <td>
                      <div style={{fontSize:13}}>{ev.venue_name||"—"}</div>
                      {ev.city && <div className="text-muted" style={{fontSize:11}}>{ev.city}</div>}
                    </td>
                    <td className="nowrap" style={{fontSize:13}}>{fmtDate(ev.start_at)}</td>
                    <td className="mono">{Number(ev.tickets_sold||0).toLocaleString()}</td>
                    <td className="mono">{Number(ev.booking_count||0).toLocaleString()}</td>
                    <td className="mono nowrap amount-cell">{fmt(ev.gross_revenue)}</td>
                    <td className="text-center">
                      {ev.is_live ? <span className="badge bg-success">Live</span> : <span className="text-muted">—</span>}
                    </td>
                    <td>
                      <div className="ev-actions">
                        <button className="ev-btn ev-btn-edit" title="Edit" disabled={!!savingId} onClick={()=>handleEdit(ev)}>
                          <i className="bi bi-pencil" />
                        </button>
                        <button className="ev-btn ev-btn-delete" title="Delete" disabled={!!savingId} onClick={()=>handleDelete(ev)}>
                          <i className="bi bi-trash" />
                        </button>
                        {!ev.is_live && (
                          <button className="ev-btn ev-btn-live" title="Set Live" disabled={!!savingId} onClick={()=>handleSetLive(ev)}>
                            <i className="bi bi-broadcast" />
                          </button>
                        )}
                        <button className="ev-btn ev-btn-toggle" title="Manage Tiers & Screenings"
                          onClick={()=>setExpandedId(expandedId===ev.id?null:ev.id)}>
                          <i className={`bi bi-chevron-${expandedId===ev.id?"up":"down"}`} />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* EXPANDED: TIERS + SCREENINGS */}
                  {expandedId===ev.id && (
                    <tr key={`${ev.id}-exp`}>
                      <td colSpan={10} style={{background:"var(--bs-light,#f8f9fa)",padding:"12px 24px"}}>
                        <div className="row g-3">
                          {/* Ticket Tiers */}
                          <div className="col-md-6">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong style={{fontSize:13}}>Ticket Tiers</strong>
                              <button className="ev-header-btn" onClick={()=>handleAddTier(ev.id)}>
                                <i className="bi bi-plus-lg" />Add Tier
                              </button>
                            </div>
                            {(!ev.ticket_tiers || ev.ticket_tiers.length===0)
                              ? <p className="text-muted small mb-0">No tiers yet.</p>
                              : <table className="table table-sm table-bordered mb-0" style={{fontSize:12}}>
                                  <thead className="table-light"><tr><th>Name</th><th>Price</th><th>Seats</th><th></th></tr></thead>
                                  <tbody>
                                    {ev.ticket_tiers.map(t=>(
                                      <tr key={t.id}>
                                        <td>{t.name}</td>
                                        <td className="mono">{fmt(t.price)}</td>
                                        <td>{t.available_seats}</td>
                                        <td>
                                          <button className="ev-btn ev-btn-delete" style={{width:28,height:28,fontSize:".8rem"}} onClick={()=>handleDeleteTier(t.id)}>
                                            <i className="bi bi-trash" />
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                            }
                          </div>
                          {/* Screenings */}
                          <div className="col-md-6">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong style={{fontSize:13}}>Screenings</strong>
                              <button className="ev-header-btn" onClick={()=>handleAddScreening(ev.id)}>
                                <i className="bi bi-plus-lg" />Add Screening
                              </button>
                            </div>
                            <p className="text-muted small mb-0">Use the Add Screening button to create new show slots for this event.</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages>1 && (
          <div className="card-footer d-flex justify-content-between align-items-center py-2 px-3">
            <small className="text-muted">Page {page} of {totalPages} · {total} events</small>
            <div className="d-flex gap-1">
              <button className="btn btn-sm btn-outline-secondary" disabled={page<=1} onClick={()=>handlePage(page-1)}>&lsaquo; Prev</button>
              {Array.from({length:Math.min(5,totalPages)},(_,i)=>{
                const start=Math.max(1,Math.min(page-2,totalPages-4)); const p=start+i;
                return p<=totalPages ? <button key={p} className={`btn btn-sm ${p===page?"btn-primary":"btn-outline-secondary"}`} onClick={()=>handlePage(p)}>{p}</button> : null;
              })}
              <button className="btn btn-sm btn-outline-secondary" disabled={page>=totalPages} onClick={()=>handlePage(page+1)}>Next &rsaquo;</button>
            </div>
          </div>
        )}
      </div>

      <footer className="dashboard-footer mt-auto">
        <small>© {new Date().getFullYear()} <strong>སྟབས་བདེ། </strong> Admin • All rights reserved</small>
      </footer>
    </>
  );
}
